import { useCallback, useEffect, useRef, useState } from 'react';

import { Col, Input, Row, Select, Switch, Tooltip, Typography } from 'antd';

import clsx from 'clsx';
import Alert from 'components/Alert';
import CustomMaskedInput from 'components/MaskedInput';
import type IProduct from 'interfaces/IProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import { EChargeType, EComissionType } from 'v2/interfaces/product';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { Recruitment } from './Recruitment';
import { SelectIntroduction } from './select';
// import { SubscriptionPlans } from './SubscriptionPlans';
import { CustomTooltip } from '../CustomTooltip';
import { FormContent } from '../FormContent';
import Validations from './validations';

interface IProps {
  loading: boolean;
  handleChange: any;
  product: IProduct;
  copyFromOriginal?: any;
  modifieds: any;
  editing: any;
  fade: any;
  onChangeRequiredFields?: any;
  userHasSubscriptionModule: boolean;
  updateProduct: (product: IProduct) => void;
}

function Affiliate(props: IProps) {
  const { loading, product, handleChange, modifieds, userHasSubscriptionModule, onChangeRequiredFields } = props;

  const [validationErrors, setValidationErrors] = useState(() => run(props.product, Validations));

  const prevProductRef = useRef(product);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return validationErrors[field] ? true : false;
    },
    [validationErrors]
  );

  useEffect(() => {
    if (props.loading) {
      return;
    }

    if (props.product === prevProductRef.current) {
      return;
    }

    const newValidationErrors = run(props.product, Validations);
    setValidationErrors(newValidationErrors);
    prevProductRef.current = props.product;

    if (onChangeRequiredFields) {
      onChangeRequiredFields(
        'affiliateRequiredFields',
        newValidationErrors ? Object.keys(newValidationErrors).length : 0
      );
    }
  }, [props.loading, props.product, onChangeRequiredFields]);

  const handleChangeCommissionFirstInvoice = useCallback(
    async (e: any) => {
      const confirm = await Alert.show({
        message:
          'Verificamos que houve alteração na forma de comissionamento. Será enviado um e-mail aos afiliados cadastrados no produto.',
        title: 'Alteração de comissão em primeira fatura',
        confirmation: true
      });
      return handleChange('comission_on_first_invoice', confirm ? e.target.checked : !e.target.checked);
    },
    [handleChange]
  );

  if (!product?.allow_affiliation) {
    return (
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <FormContent title='' loading={loading}>
            <Row gutter={[16, 16]} align='middle'>
              <Col span={24}>
                <SelectIntroduction onAccept={() => handleChange('allow_affiliation', true)} />
              </Col>
            </Row>
          </FormContent>
        </Col>
      </Row>
    );
  }

  const commissionTextOnFirstInvoice = () => {
    if (!product.membership_fee && product.trial) {
      return 'Habilitar comissionamento somente na primeira fatura paga.';
    }

    if (product.membership_fee) {
      return 'Habilitar comissionamento somente na taxa de adesão.';
    }

    return 'Habilitar comissionamento somente na primeira fatura';
  };

  const isBetaSubscriptionsFirstInvoice = eduzzAccountsService.verifyUserHasBetaModule('comissao-primeira-fatura');
  const isActiveAffiliateNew = !!modifieds?.allow_affiliation;
  const canEditInfiniteFunnel = isActiveAffiliateNew || !!modifieds?.infinity_funnel || !product.infinity_funnel;
  const isVariation = product.isVariation();
  const isMainWithChildren = product.isMainWithChildren();
  const isService = product.cnt_partner_cod === EPartnerCode.SERVICE;
  const noEditMessage = 'Só é possível alterar o campo em configurações gerais';
  const noEditIfIsServiceMessage = 'Produtos do tipo serviço não são exibidos na vitrine de afiliados';
  const cannotConfigureShowcase = isService || isVariation;
  const tooltipMessage = isVariation ? noEditMessage : noEditIfIsServiceMessage;
  const activeAfiliateMessage = isActiveAffiliateNew
    ? 'A opção venda por afiliados foi habilitada. Ao salvar o produto não será possível desabilita-la.'
    : 'A opção venda por afiliados foi habilitada. Não é possível desabilitar  vendas por afiliados.';

  const isFree = product.charge_type === EChargeType.FREE;
  const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;

  const isRecurringComissionPercentage = product.recurring_comission_type === EComissionType.PERCENTAGE;
  const isRecurringComissionValue = product.recurring_comission_type === EComissionType.VALUE;

  const allowRecurringComission = isSubscription && !isFree && !isMainWithChildren;
  const allowRecurringComissionPercentage = allowRecurringComission && isRecurringComissionPercentage;
  const allowRecurringComissionValue = allowRecurringComission && isRecurringComissionValue;
  const isNotComissionOnFirstInvoice = !product.membership_fee || !product.comission_on_first_invoice;
  const isPercentage = product.membership_comission_type === EComissionType.PERCENTAGE;
  const showSubscriptionFirstInvoice = isSubscription && isPercentage && isBetaSubscriptionsFirstInvoice;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <FormContent title='Afiliação' loading={loading}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <Tooltip title={activeAfiliateMessage} placement='left'>
                <div className='flex items-center gap-2'>
                  <Switch
                    disabled={!isActiveAffiliateNew}
                    checked={product?.allow_affiliation}
                    onChange={checked => handleChange('allow_affiliation', checked)}
                  />
                  <Typography.Text className='text-base'>Permitir venda por afiliado</Typography.Text>
                </div>
              </Tooltip>
              <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                {activeAfiliateMessage}{' '}
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/7336792418203-Como-configurar-as-comiss%C3%B5es-para-afiliados#h_01HH2T0MH1Q4CE4KBJD604PK1X'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Saiba Mais
                </a>
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <CustomTooltip show={cannotConfigureShowcase} message={tooltipMessage}>
                <div className='flex items-center gap-2'>
                  <Switch
                    disabled={cannotConfigureShowcase}
                    checked={product.visible}
                    onChange={checked => handleChange('visible', checked)}
                  />
                  <Typography.Text className='text-base'>Visível na vitrine</Typography.Text>
                </div>
              </CustomTooltip>
              <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                Conteúdo aparecerá na vitrine disponível para afiliação.
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <CustomTooltip show={isVariation} message={noEditMessage}>
                <div className='flex items-center gap-2'>
                  <Switch
                    disabled={isVariation}
                    checked={product.auto_affiliation}
                    onChange={checked => handleChange('auto_affiliation', checked)}
                  />
                  <Typography.Text className='text-base'>Afiliação automática</Typography.Text>
                </div>
              </CustomTooltip>
              <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                Solicitações de afiliação serão aprovadas automaticamente.
              </Typography.Paragraph>
            </Col>
            {showSubscriptionFirstInvoice && (
              <Col span={24}>
                <CustomTooltip show={isVariation} message={noEditMessage}>
                  <div className='flex items-center gap-2'>
                    <Switch
                      checked={product.comission_on_first_invoice}
                      onChange={checked => handleChangeCommissionFirstInvoice({ target: { checked } })}
                    />
                    <Typography.Text className='text-base'>
                      {!product.membership_fee ? 'Primeira fatura' : 'Taxa de adesão'}
                    </Typography.Text>
                  </div>
                </CustomTooltip>
                <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                  {commissionTextOnFirstInvoice()}
                </Typography.Paragraph>
              </Col>
            )}

            {allowRecurringComission && isNotComissionOnFirstInvoice && !userHasSubscriptionModule && (
              <Col span={24}>
                <div className='relative'>
                  <Typography.Text className='mb-1 block text-base'>
                    {!product.comission_on_first_invoice ? 'Tipo de comissão recorrente' : 'Tipo de comissão'}
                  </Typography.Text>
                  <Select
                    id='recurring_comission_type'
                    className='w-full'
                    value={product.recurring_comission_type}
                    onChange={value => handleChange('recurring_comission_type', value)}
                    status={isValid('recurring_comission_type') ? 'error' : undefined}
                    disabled={product.recurring_comission_type === EComissionType.PERCENTAGE}
                    options={[
                      { value: EComissionType.PERCENTAGE, label: 'Porcentagem' },
                      ...(product.recurring_comission_type === EComissionType.VALUE
                        ? [{ value: EComissionType.VALUE, label: 'Valor', disabled: true }]
                        : [])
                    ]}
                  />
                  <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                    Defina o percentual de comissão.
                  </Typography.Paragraph>
                </div>
              </Col>
            )}

            {allowRecurringComissionPercentage && isNotComissionOnFirstInvoice && !userHasSubscriptionModule && (
              <Col span={24}>
                <CustomMaskedInput
                  fullWidth
                  label={
                    !product.comission_on_first_invoice ? 'Percentual da comissão recorrente' : 'Percentual da comissão'
                  }
                  helperText={isValid('recurring_comission_value') ? errorFor('recurring_comission_value') : ''}
                  error={errorFor('recurring_comission_value')}
                  disableUnderline
                  InputLabelProps={{ shrink: true }}
                  className='relative'
                  mask='percent'
                  startAdornment='%'
                  value={product.recurring_comission_value}
                  onChange={(value: any) => handleChange('recurring_comission_value', value)}
                />
              </Col>
            )}

            {allowRecurringComissionValue && !product.comission_on_first_invoice && (
              <Col span={24}>
                <CustomMaskedInput
                  fullWidth
                  label='Valor da comissão recorrente'
                  helperText={isValid('recurring_comission_value') ? errorFor('recurring_comission_value') : ''}
                  error={errorFor('recurring_comission_value')}
                  disableUnderline
                  InputLabelProps={{ shrink: true }}
                  className='relative'
                  mask='money'
                  startAdornment='R$'
                  value={product.recurring_comission_value}
                  onChange={(value: any) => handleChange('recurring_comission_value', value)}
                />
              </Col>
            )}

            {!isSubscription && !isMainWithChildren && (
              <Col span={24}>
                <div className='relative'>
                  <Typography.Text className='mb-1 block text-base'>
                    {product.charge_type === EChargeType.SUBSCRIPTION ? 'Comissão da Adesão' : 'Tipo de comissão'}
                  </Typography.Text>
                  <Select
                    id='comission_type'
                    className='w-full'
                    value={product.comission_type}
                    onChange={value => handleChange('comission_type', value)}
                    status={isValid('comission_type') ? 'error' : undefined}
                    disabled={product.comission_type === EComissionType.PERCENTAGE}
                    options={[
                      { value: EComissionType.PERCENTAGE, label: 'Porcentagem' },
                      ...(product.comission_type === EComissionType.VALUE && product.charge_type !== EChargeType.OTHER
                        ? [{ value: EComissionType.VALUE, label: 'Valor', disabled: true }]
                        : [])
                    ]}
                  />
                  <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                    Comissão com percentual do ganho bruto.
                  </Typography.Paragraph>
                </div>
              </Col>
            )}

            {isSubscription && product.membership_fee && (
              <Col span={24}>
                <div className='relative'>
                  <Typography.Text className='mb-1 block text-base'>Tipo de comissão da adesão</Typography.Text>
                  <Select
                    id='membership_comission_type'
                    className='w-full'
                    value={product.membership_comission_type}
                    onChange={value => handleChange('membership_comission_type', value)}
                    disabled={product.membership_comission_type === EComissionType.PERCENTAGE}
                    options={[
                      { value: EComissionType.PERCENTAGE, label: 'Porcentagem' },
                      ...(product.membership_comission_type === EComissionType.VALUE
                        ? [{ value: EComissionType.VALUE, label: 'Valor', disabled: true }]
                        : [])
                    ]}
                  />
                  <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                    Defina o percentual de comissão.
                  </Typography.Paragraph>
                </div>
              </Col>
            )}

            {!isSubscription && product.comission_type === EComissionType.PERCENTAGE && !isMainWithChildren && (
              <Col span={24}>
                <CustomMaskedInput
                  fullWidth
                  className='relative'
                  helperText={isValid('comission_value') ? errorFor('comission_value') : ''}
                  label='Percentual da comissão'
                  disableUnderline
                  InputLabelProps={{ shrink: true }}
                  mask='percent'
                  startAdornment='%'
                  value={product.comission_value}
                  onChange={(value: any) => handleChange('comission_value', value)}
                  error={isValid('comission_value')}
                />
              </Col>
            )}

            {!isSubscription && product.comission_type === EComissionType.VALUE && !isMainWithChildren && (
              <Col span={24}>
                <CustomMaskedInput
                  fullWidth
                  className='relative'
                  helperText={isValid('comission_value') ? errorFor('comission_value') : ''}
                  label='Valor da comissão'
                  disableUnderline
                  InputLabelProps={{ shrink: true }}
                  mask='money'
                  startAdornment='R$'
                  value={product.comission_value}
                  onChange={(value: any) => handleChange('comission_value', value)}
                  error={isValid('comission_value')}
                />
              </Col>
            )}

            {product.charge_type === EChargeType.SUBSCRIPTION &&
              product.membership_fee &&
              product.membership_comission_type === EComissionType.PERCENTAGE && (
                <Col span={24}>
                  <CustomMaskedInput
                    fullWidth
                    label='Percentual da comissão de adesão'
                    helperText={isValid('membership_comission_value') ? errorFor('membership_comission_value') : ''}
                    error={isValid('membership_comission_value')}
                    disableUnderline
                    InputLabelProps={{ shrink: true }}
                    className='relative'
                    mask='percent'
                    startAdornment='%'
                    value={product.membership_comission_value}
                    onChange={(value: any) => handleChange('membership_comission_value', value)}
                  />
                </Col>
              )}
            {!isMainWithChildren && (
              <Col span={24}>
                <div className='flex items-center gap-2'>
                  <Switch
                    disabled={!canEditInfiniteFunnel}
                    checked={product.infinity_funnel}
                    onChange={checked => handleChange('infinity_funnel', checked)}
                  />
                  <Typography.Text className='text-base'>Funil infinito</Typography.Text>
                </div>
                <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                  A opção de funil infinito não pode ser desativada uma vez que for habilitada.
                </Typography.Paragraph>
              </Col>
            )}

            {product.infinity_funnel && !isMainWithChildren && (
              <Col span={24}>
                <div className='relative'>
                  <Typography.Text className='mb-1 block text-base'>Comissão de funil</Typography.Text>
                  <Select
                    className='w-full'
                    disabled={!canEditInfiniteFunnel}
                    value={product.funnel_commission}
                    onChange={value => handleChange('funnel_commission', value)}
                    options={[
                      { value: 50, label: 'Meia comissão' },
                      { value: 100, label: 'Comissão inteira' }
                    ]}
                  />
                </div>
              </Col>
            )}

            {userHasSubscriptionModule && isSubscription && (
              <Col span={24}>
                <Recruitment
                  loading={loading}
                  product={product}
                  handleChange={handleChange}
                  validInput={isValid}
                  errorFor={errorFor}
                  classes={{}}
                  isSubscription={isSubscription}
                  userHasSubscriptionModule={userHasSubscriptionModule}
                />
              </Col>
            )}
          </Row>
        </FormContent>
      </Col>

      {/* {userHasSubscriptionModule && isSubscription && <SubscriptionPlans loading={loading} product={product} updateProduct={updateProduct} />} */}

      <Col span={24}>
        <FormContent title='Instruções e Termos' loading={loading}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <div className='relative'>
                <Typography.Text className='mb-1 block text-base'>Instruções para afiliados</Typography.Text>
                <Input.TextArea
                  id='affiliate_instructions'
                  rows={5}
                  className='w-full'
                  maxLength={4000}
                  value={product.affiliate_instructions}
                  onChange={e => handleChange('affiliate_instructions', e.target.value)}
                />
                <Typography.Paragraph
                  className={clsx(
                    'text-content-subtitle mt-1! mb-0! text-base',
                    isValid('affiliate_instructions') ? 'text-(--uizz-antd-color-error)' : 'text-content-subtitle'
                  )}
                >
                  {isValid('affiliate_instructions')
                    ? errorFor('affiliate_instructions')
                    : 'Ex: instruções de divulgação para afiliados'}
                </Typography.Paragraph>
              </div>
            </Col>
            {!isVariation && (
              <Col span={24}>
                <div className='relative'>
                  <Typography.Text className='mb-1 block text-base'>Contrato para afiliado</Typography.Text>
                  <Input.TextArea
                    id='affiliation_contract'
                    rows={5}
                    className='w-full'
                    value={product.affiliation_contract}
                    onChange={e => handleChange('affiliation_contract', e.target.value)}
                  />
                  <Typography.Paragraph className='text-content-subtitle mt-1! mb-0! text-base'>
                    Ex: termos de aceitação para afiliados
                  </Typography.Paragraph>
                </div>
              </Col>
            )}
          </Row>
        </FormContent>
      </Col>

      {!isFree && !isVariation && !userHasSubscriptionModule && !isSubscription && (
        <Col span={24}>
          <FormContent title='Recrutamento' loading={loading}>
            <Recruitment
              loading={loading}
              product={product}
              handleChange={handleChange}
              validInput={isValid}
              errorFor={errorFor}
              classes={{}}
              isSubscription={isSubscription}
              userHasSubscriptionModule={userHasSubscriptionModule}
            />
          </FormContent>
        </Col>
      )}
    </Row>
  );
}

export default Affiliate;
